import { useState } from "react";
import axios from "axios";
import { Lock, RefreshCw, ArrowLeft, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const [passcode, setPasscode] = useState("");
  const [quotes, setQuotes] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async (code = passcode) => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/admin/quotes`, { headers: { "X-Admin-Passcode": code } });
      setQuotes(res.data);
    } catch (e) {
      setQuotes(null);
      setError(e.response?.status === 401 ? "Passcode non valido." : "Errore di connessione.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="admin-page" className="marble-bg-soft min-h-screen px-6 py-24 text-[#F8F5F0]">
      <div className="mx-auto max-w-4xl">
        <a href="/" data-testid="admin-back-link" className="mb-10 inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#A3A3A3] transition-colors hover:text-[#E5CAA4]">
          <ArrowLeft size={14} /> Torna al sito
        </a>
        <h1 className="font-display text-4xl font-medium tracking-tight sm:text-5xl">
          Richieste di <span className="gold-text italic">preventivo</span>
        </h1>
        <p className="mt-4 text-sm font-light text-[#A3A3A3]">Pannello interno Recensio — accesso riservato.</p>

        <div className="mt-10 flex max-w-md gap-3">
          <div className="relative flex-1">
            <Lock size={15} className="absolute left-0 top-1/2 -translate-y-1/2 text-[#C19D60]" />
            <input
              type="password"
              data-testid="admin-passcode-input"
              className="lux-input pl-7"
              placeholder="Passcode admin"
              value={passcode}
              autoComplete="off"
              onChange={(e) => setPasscode(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
            />
          </div>
          <button
            onClick={() => load()}
            disabled={!passcode || loading}
            data-testid="admin-unlock-btn"
            className="border border-[#C19D60]/50 px-6 text-[11px] uppercase tracking-[0.2em] text-[#E5CAA4] transition-colors duration-500 hover:bg-[#C19D60] hover:text-[#050505] disabled:opacity-40"
          >
            {loading ? "…" : "Sblocca"}
          </button>
        </div>
        {error && <p data-testid="admin-error" className="mt-4 text-sm text-red-400">{error}</p>}

        {quotes && (
          <div className="mt-14" data-testid="admin-quotes-list">
            <div className="mb-6 flex items-center justify-between">
              <p className="text-[11px] uppercase tracking-[0.3em] text-[#C19D60]">{quotes.length} richieste</p>
              <button onClick={() => load()} data-testid="admin-refresh-btn" className="flex items-center gap-2 text-xs text-[#A3A3A3] transition-colors hover:text-[#E5CAA4]">
                <RefreshCw size={13} /> Aggiorna
              </button>
            </div>
            {quotes.length === 0 && <p className="text-sm font-light text-[#A3A3A3]">Nessuna richiesta ricevuta finora.</p>}
            <div className="space-y-px bg-[#C19D60]/10">
              {quotes.map((q) => (
                <article key={q.id} data-testid={`admin-quote-${q.id}`} className="card-surface p-6">
                  <div className="flex flex-wrap items-baseline justify-between gap-3">
                    <h3 className="font-display text-xl font-medium">{q.name}{q.business ? ` — ${q.business}` : ""}</h3>
                    <div className="flex items-center gap-3">
                      <time className="text-xs font-light text-[#666]">{new Date(q.created_at).toLocaleString("it-IT")}</time>
                      <button
                        data-testid={`admin-quote-delete-${q.id}`}
                        aria-label="Elimina richiesta"
                        onClick={async () => {
                          if (!window.confirm(`Eliminare la richiesta di ${q.name}? I dati saranno cancellati definitivamente.`)) return;
                          await axios.delete(`${API}/admin/quotes/${q.id}`, { headers: { "X-Admin-Passcode": passcode } });
                          setQuotes((prev) => prev.filter((x) => x.id !== q.id));
                        }}
                        className="text-[#666] transition-colors hover:text-red-400"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-xs font-light text-[#A3A3A3]">
                    <span>{q.email}</span>
                    {q.phone && <span>{q.phone}</span>}
                    {q.service && <span className="text-[#C19D60]">{q.service}</span>}
                  </div>
                  <p className="mt-3 text-sm font-light leading-relaxed text-[#c9c9c9]">{q.message}</p>
                </article>
              ))}
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
